import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

import { getProducts } from '../../api/products';
import { getEvaluation } from '../../api/url_data_agent_evaluations';
import { getLiveMatchCandidates } from '../../helpers/url_data_agent_live_matches_helpers';
import Loader from '../General/Loader';
import SafeImage from './SafeImage';

import './LiveMatchProductPicker.scss';

// Standalone picker: the URL-matching page's SearchProducts is bound to its own contexts, so this
// reuses the same getProducts search + query seeding without them.
const LiveMatchProductPicker = ({ row, note, onPick, onClose, isSaving }) => {
  const shortProductName = (row.agentProductName || row.productName || '')
    .trim()
    .split(' ')
    .slice(0, 3)
    .join(' ');
  const seededTerm = `${row.agentBrandName || ''} ${shortProductName}`.replaceAll('null', '').trim();
  const [searchTerm, setSearchTerm] = useState(seededTerm);
  const [debouncedValue, setDebouncedValue] = useState(seededTerm);
  const [products, setProducts] = useState([]);
  const [candidates, setCandidates] = useState(() => getLiveMatchCandidates(row));
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(Boolean(row.id) && !row.agentRunJson);
  const [candidateError, setCandidateError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!row.id || row.agentRunJson) return undefined;
    let cancelled = false;
    getEvaluation(row.id)
      .then(({ evaluation }) => {
        if (!cancelled) setCandidates(getLiveMatchCandidates(evaluation));
      })
      .catch(() => {
        if (!cancelled) setCandidateError('Could not load the APM shortlist. You can still search the catalog.');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCandidates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row]);

  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedValue.length <= 2) {
      setProducts([]);
      return undefined;
    }
    let cancelled = false;
    const doSearch = async () => {
      setIsLoading(true);
      try {
        const { products: results } = await getProducts({ query: debouncedValue, pageSize: 40 });
        if (!cancelled) setProducts(results || []);
      } catch {
        if (!cancelled) window.ALERT.error('Failed to search products. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    doSearch();
    return () => {
      cancelled = true;
    };
  }, [debouncedValue]);

  return (
    <div className='live-match-picker-overlay' onClick={onClose}>
      <div className='live-match-picker' role='dialog' aria-label='Choose catalog product' onClick={e => e.stopPropagation()}>
        <div className='lmp-head'>
          <strong>Pick the correct product</strong>
          <span>{note || (row.applyDecision === 'proposed' ? 'Your pick matches this link and completes its APM review.' : 'Your pick replaces the current match.')}</span>
          <button type='button' className='lmp-close' aria-label='Close product picker' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className='lmp-input-wrap'>
          <input
            type='text'
            value={searchTerm}
            placeholder='Search products…'
            aria-label='Search products'
            spellCheck={false}
            onChange={e => setSearchTerm(e.target.value)}
            ref={inputRef}
          />
          {isLoading && <Loader variant='circle' size={18} className='lmp-loader' />}
        </div>
        <ul className='lmp-results'>
          {isLoadingCandidates && <li className='lmp-empty'>Loading APM shortlist…</li>}
          {candidateError && (
            <li className='lmp-empty' role='status'>
              {candidateError}
            </li>
          )}
          {[
            { label: 'APM shortlist', products: candidates.map(candidate => ({ ...candidate, id: candidate.Product_id })) },
            { label: 'Catalog search results', products }
          ].map(
            group =>
              group.products.length > 0 && (
                <React.Fragment key={group.label}>
                  <li className='lmp-group-label'>{group.label}</li>
                  {group.products.map(product => (
                    <li key={product.id} className='lmp-result'>
                      <SafeImage source={product.image} placeholderText='' alt='' />
                      <div className='lmp-result-text'>
                        <span className='lmp-result-brand'>{product.brand}</span>
                        <span className='lmp-result-title'>{product.title}</span>
                        <span className='lmp-result-sub'>
                          #{product.id}
                          {product.totalMentionCount != null ? ` · ${product.totalMentionCount} promoters` : ''}
                        </span>
                      </div>
                      <button type='button' disabled={isSaving} onClick={() => onPick(product)}>
                        {isSaving ? 'Matching…' : 'Use this product'}
                      </button>
                    </li>
                  ))}
                </React.Fragment>
              )
          )}
          {!isLoading && debouncedValue.length > 2 && !products.length && (
            <li className='lmp-empty'>No products match this search — try fewer words.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

LiveMatchProductPicker.propTypes = {
  row: PropTypes.object.isRequired,
  note: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isSaving: PropTypes.bool
};

export default LiveMatchProductPicker;
